import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ClinicalReport,
  ClinicalReportInconsistencia,
  ClinicalTemplateWarning,
} from '../models/clinical-report.model';
import { ClinicalReportPromptVersion } from '../models/clinical-report-prompt-version.model';
import { ClinicalTextTemplate } from '../models/clinical-text-template.model';
import { ClinicalReportCapabilities } from '../models/clinical-report-capabilities.model';

// The exact payload a generation sends to the model. The repository builds it from the request
// (`previewPayload`) and accepts it back edited as `ClinicalReportRequest.promptOverride`, so the
// developer prompt lab previews and overrides through one shape: what it shows is, by
// construction, what gets sent.
export interface ClinicalReportPromptDraft {
  readonly model: string;
  // null = leave `temperature` unset and let the model use its own default.
  readonly temperature: number | null;
  readonly systemInstruction: string;
  readonly prompt: string;
}

export interface ClinicalReportPreview {
  readonly draft: ClinicalReportPromptDraft;
  // The response schema serialized for display only: which schema applies still follows from
  // `template`, never from the (editable) system instruction.
  readonly schemaJson: string;
}

export interface ClinicalReportDebugInfo {
  // What was actually sent, override included -- not what `buildDraft` would have produced.
  readonly draft: ClinicalReportPromptDraft;
  readonly rawResponse: string;
}

export interface ClinicalReportRequest {
  readonly consultation: Record<string, unknown>;
  readonly freeText: string;
  // BCP 47 code from the active panel locale. It only decides a free-text report's language when
  // there are no selected notes; a clinic template remains the source of truth for its own language.
  readonly interfaceLanguage?: string;
  readonly template: ClinicalTextTemplate | null;
  // The saved prompt revision this generation runs with. Supplied by the CALLER rather than read
  // by the repository itself, because the catalogue lives in a presentation-layer store and
  // `data/` may not import `presentation/` -- and because the endpoint behind it is developer-only,
  // so a regular clinic session must never fetch it just to generate a report. Absent or null, the
  // repository falls back to the shipped `CLINICAL_*_SYSTEM_INSTRUCTION` constants and to the
  // prompt composition that predates the catalogue.
  readonly promptVersion?: ClinicalReportPromptVersion | null;
  // Both optional and both developer-only (the prompt lab). Absent, generation behaves exactly as
  // it did before they existed.
  readonly promptOverride?: ClinicalReportPromptDraft | null;
  readonly captureDebug?: boolean;
  // Developer-only model picker. Absent, the repository falls back to `DEFAULT_CLINICAL_REPORT_AI_MODEL`.
  readonly model?: string;
}

export type ClinicalReportResult =
  | {
      readonly kind: 'structured';
      readonly report: ClinicalReport;
      readonly debug: ClinicalReportDebugInfo | null;
    }
  | {
      readonly kind: 'template';
      readonly text: string;
      readonly datosNoEncontrados: readonly string[];
      readonly inconsistencias: readonly ClinicalReportInconsistencia[];
      // Deterministic, non-AI cross-check of the AI's own answer against the template's fixed text
      // and placeholder options (`verifyFilledTemplate`) -- kept separate from `inconsistencias`,
      // which are the MODEL's own report of contradictions in the source data.
      readonly warnings: readonly ClinicalTemplateWarning[];
      readonly debug: ClinicalReportDebugInfo | null;
    };

export interface IClinicalReportRepository {
  getCapabilities(): Observable<ClinicalReportCapabilities>;
  generate(request: ClinicalReportRequest): Observable<ClinicalReportResult>;
  // Async because the response schema is built through the same lazy `import('firebase/ai')` the
  // generation itself uses.
  previewPayload(request: ClinicalReportRequest): Promise<ClinicalReportPreview>;
}

export const CLINICAL_REPORT_REPOSITORY = new InjectionToken<IClinicalReportRepository>(
  'IClinicalReportRepository',
);
